// -- Rotary Knob Widget --
// Replaces range inputs with drag-to-adjust circular knobs.
// Drag up = increase, drag down = decrease. Scroll wheel also works.

const ARC_START = 135;   // degrees, 7-o'clock position
const ARC_TOTAL = 270;   // total arc sweep

function polarXY(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

function arcD(cx: number, cy: number, r: number, start: number, end: number): string {
  const sweep = end - start;
  if (sweep < 0.5) return '';
  const [sx, sy] = polarXY(cx, cy, r, start);
  const [ex, ey] = polarXY(cx, cy, r, end);
  return `M ${sx} ${sy} A ${r} ${r} 0 ${sweep > 180 ? 1 : 0} 1 ${ex} ${ey}`;
}

export interface KnobConfig {
  el: HTMLInputElement;
  val: HTMLElement;
  fmt: (v: string) => string;
}

// Track the active knob globally to avoid conflicts
let activeKnob: KnobWidget | null = null;

export class KnobWidget {
  private input: HTMLInputElement;
  private valEl: HTMLElement;
  private fmt: (v: string) => string;
  private fillArc: SVGPathElement;
  private indicator: SVGLineElement;
  private titleEl: SVGTitleElement;
  private knobBody: SVGCircleElement;
  private min: number;
  private max: number;
  private step: number;
  private dragging = false;
  private startY = 0;
  private startNorm = 0;
  // Pixels of vertical drag to traverse the full range (~75% of screen height)
  private readonly pxRange = window.innerHeight * 0.6;

  constructor(cfg: KnobConfig, label: string) {
    this.input = cfg.el;
    this.valEl = cfg.val;
    this.fmt = cfg.fmt;
    this.min = parseFloat(cfg.el.min);
    this.max = parseFloat(cfg.el.max);
    this.step = parseFloat(cfg.el.step) || 1;


    cfg.el.style.display = 'none';

    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '0 0 40 40');
    svg.setAttribute('width', '36');
    svg.setAttribute('height', '36');
    svg.classList.add('knob-svg');

    // Tooltip
    this.titleEl = document.createElementNS(NS, 'title');
    svg.appendChild(this.titleEl);

    // Track arc
    const track = document.createElementNS(NS, 'path');
    track.setAttribute('fill', 'none');
    track.setAttribute('stroke', '#4b5563');
    track.setAttribute('stroke-width', '2.5');
    track.setAttribute('stroke-linecap', 'round');
    track.setAttribute('d', arcD(20, 20, 15, ARC_START, ARC_START + ARC_TOTAL));

    // Fill arc
    this.fillArc = document.createElementNS(NS, 'path');
    this.fillArc.setAttribute('fill', 'none');
    this.fillArc.setAttribute('stroke', '#60a5fa');
    this.fillArc.setAttribute('stroke-width', '2.5');
    this.fillArc.setAttribute('stroke-linecap', 'round');

    // Body circle
    this.knobBody = document.createElementNS(NS, 'circle');
    this.knobBody.setAttribute('cx', '20');
    this.knobBody.setAttribute('cy', '20');
    this.knobBody.setAttribute('r', '11');
    this.knobBody.setAttribute('fill', '#1e293b');
    this.knobBody.setAttribute('stroke', '#334155');
    this.knobBody.setAttribute('stroke-width', '0.75');
    this.knobBody.classList.add('knob-body');

    // Indicator line from center
    this.indicator = document.createElementNS(NS, 'line');
    this.indicator.setAttribute('x1', '20');
    this.indicator.setAttribute('y1', '20');
    this.indicator.setAttribute('stroke', '#e2e8f0');
    this.indicator.setAttribute('stroke-width', '1.5');
    this.indicator.setAttribute('stroke-linecap', 'round');

    svg.append(track, this.fillArc, this.knobBody, this.indicator);

    // Insert before the hidden input
    cfg.el.parentElement!.insertBefore(svg, cfg.el);

    // Mouse down: begin drag with absolute positioning
    svg.addEventListener('mousedown', (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (activeKnob && activeKnob !== this) return;
      this.dragging = true;
      this.startY = e.clientY;
      this.startNorm = this.normalized;
      activeKnob = this;
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    });

    // Double-click: reset to middle
    svg.addEventListener('dblclick', (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.setFromNorm(0.5);
    });

    // Scroll wheel
    svg.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const dir = e.deltaY < 0 ? 1 : -1;
      this.setFromNorm(this.normalized + dir * (this.step / (this.max - this.min)) * 3);
    }, { passive: false });

    // Touch start
    svg.addEventListener('touchstart', (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (activeKnob && activeKnob !== this) return;
      this.dragging = true;
      this.startY = e.touches[0].clientY;
      this.startNorm = this.normalized;
      activeKnob = this;
    }, { passive: false });

    // Sync from external changes (+/- buttons, restore, etc.)
    this.input.addEventListener('input', () => this.render());

    this._label = label;
    this.render();
  }

  private _label = '';

  private get value(): number { return parseFloat(this.input.value); }

  private get normalized(): number {
    return (this.value - this.min) / (this.max - this.min);
  }

  private setFromNorm(norm: number): void {
    const clamped = Math.max(0, Math.min(1, norm));
    let raw = this.min + clamped * (this.max - this.min);
    raw = Math.round(raw / this.step) * this.step;
    raw = Math.max(this.min, Math.min(this.max, raw));
    const v = parseFloat(raw.toFixed(10));
    if (v !== this.value) {
      this.input.value = String(v);
      this.valEl.textContent = this.fmt(String(v));
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    this.render();
  }

  // Called from global mousemove/touchmove handlers
  handleMove(clientY: number): void {
    if (!this.dragging) return;
    // Drag up = increase (negative dy), drag down = decrease (positive dy)
    const dy = this.startY - clientY;
    this.setFromNorm(this.startNorm + dy / this.pxRange);
  }

  release(): void {
    this.dragging = false;
    if (activeKnob === this) {
      activeKnob = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }

  private render(): void {
    const norm = this.normalized;
    const angle = ARC_START + norm * ARC_TOTAL;

    if (norm > 0.003) {
      this.fillArc.setAttribute('d', arcD(20, 20, 15, ARC_START, angle));
    } else {
      this.fillArc.setAttribute('d', '');
    }

    const [ix, iy] = polarXY(20, 20, 9, angle);
    this.indicator.setAttribute('x2', String(ix));
    this.indicator.setAttribute('y2', String(iy));

    this.titleEl.textContent = `${this._label}: ${this.valEl.textContent}`;
  }
}

// Global move/end handlers (set up once)
if (typeof window !== 'undefined') {
  window.addEventListener('mousemove', (e: MouseEvent) => {
    if (activeKnob) activeKnob.handleMove(e.clientY);
  });

  window.addEventListener('mouseup', () => {
    if (activeKnob) activeKnob.release();
  });

  window.addEventListener('touchmove', (e: TouchEvent) => {
    if (activeKnob) {
      e.preventDefault();
      activeKnob.handleMove(e.touches[0].clientY);
    }
  }, { passive: false });

  window.addEventListener('touchend', () => {
    if (activeKnob) activeKnob.release();
  });
}

export function initKnobs(
  sliders: Record<string, KnobConfig>,
  gaSliders: Record<string, KnobConfig>,
  extra: { cfg: KnobConfig; label: string }[],
): void {
  for (const [name, cfg] of Object.entries(sliders)) {
    new KnobWidget(cfg, name);
  }
  for (const [name, cfg] of Object.entries(gaSliders)) {
    new KnobWidget(cfg, `GA ${name}`);
  }
  for (const { cfg, label } of extra) {
    new KnobWidget(cfg, label);
  }
}
